import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { IBusiness, IContact, IContactField } from '@trii/types/dist/Contacts/contacts';
import useEditBusiness from '../hooks/useEditBusiness/useEditBusiness';
import type UseEditBusiness from '../hooks/useEditBusiness/types/UseEditBusiness';
import useEditContact from '../hooks/useEditContact/useEditContact';
import type UseEditContact from '../hooks/useEditContact/types/UseEditContact';

type EditEntityContextValue =
  | {
      entityType: 'contact';
      editEntity: UseEditContact;
      entityId: string;
    }
  | {
      entityType: 'business';
      editEntity: UseEditBusiness;
      entityId: string;
    };

const EditEntityContext = createContext<EditEntityContextValue | null>(null);

type BaseEditEntityProviderProps = {
  children: ReactNode;
  customContactFields: IContactField[];
  baseUrl?: string;
  spaceId?: string;
  onEntityUpdated?: (entity: unknown) => void;
};

type EditContactEntityProviderProps = BaseEditEntityProviderProps & {
  contact: IContact;
};

export const EditContactEntityProvider = ({
  children,
  contact,
  customContactFields,
  baseUrl,
  spaceId,
  onEntityUpdated,
}: EditContactEntityProviderProps) => {
  const editEntity = useEditContact({
    contact,
    customContactFields,
    baseUrl,
    spaceId,
    onEntityUpdated,
  });

  return (
    <EditEntityContext.Provider value={{ entityType: 'contact', editEntity, entityId: String(contact.id) }}>
      {children}
    </EditEntityContext.Provider>
  );
};

type EditBusinessEntityProviderProps = BaseEditEntityProviderProps & {
  business: IBusiness;
};

export const EditBusinessEntityProvider = ({
  children,
  business,
  customContactFields,
  baseUrl,
  spaceId,
  onEntityUpdated,
}: EditBusinessEntityProviderProps) => {
  const editEntity = useEditBusiness({
    business,
    customContactFields,
    baseUrl,
    spaceId,
    onEntityUpdated,
  });

  return (
    <EditEntityContext.Provider value={{ entityType: 'business', editEntity, entityId: String(business.id) }}>
      {children}
    </EditEntityContext.Provider>
  );
};

type EditEntityProviderProps = BaseEditEntityProviderProps & {
  entityType: 'contact' | 'business';
  entityData: IContact | IBusiness;
};

export const EditEntityProvider = ({
  children,
  entityType,
  entityData,
  customContactFields,
  baseUrl,
  spaceId,
  onEntityUpdated,
}: EditEntityProviderProps) => {
  if (entityType === 'contact') {
    return (
      <EditContactEntityProvider
        contact={entityData as IContact}
        customContactFields={customContactFields}
        baseUrl={baseUrl}
        spaceId={spaceId}
        onEntityUpdated={onEntityUpdated}
      >
        {children}
      </EditContactEntityProvider>
    );
  }

  return (
    <EditBusinessEntityProvider
      business={entityData as IBusiness}
      customContactFields={customContactFields}
      baseUrl={baseUrl}
      spaceId={spaceId}
      onEntityUpdated={onEntityUpdated}
    >
      {children}
    </EditBusinessEntityProvider>
  );
};

export const useEditEntity = () => {
  const ctx = useContext(EditEntityContext);
  if (!ctx) {
    throw new Error('useEditEntity must be used within EditEntityProvider');
  }
  return ctx;
};
